import React, { useEffect, useRef, useState, useContext } from 'react';
import Chart from 'chart.js/auto';
import { ApiContext } from '../../../../../context/MyContext';
import * as userService from '../../../../../service/userService';
import { parseLocalDate } from '../../../../../utils/formatLocalDate';
import { getBarColor ,STATUS_COLOR_MAP} from '../../../../../utils/chart.util';
const StepsGraphModal = ({ profileId, selectedDate, eventTypeId, onClose }) => {
  const chartRef = useRef(null);
  const { get } = useContext(ApiContext);

  const [range, setRange] = useState('D');
  const [stepsData, setStepsData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [average, setAverage] = useState(0);
  const [recommended, setRecommended] = useState(10000);
  const [dailyStreak, setDailyStreak] = useState(0);
  const [hasData, setHasData] = useState(true);
  const [percentages, setPercentages] = useState([]);
  const [statuses, setStatuses] = useState([]);


  const fetchChartData = async () => {
    const result = await userService.fetchStepsSummary(get, profileId, range, selectedDate, eventTypeId);
    if (result.success) {
      const data = result.data || [];
      setAverage(result.average_value || 0);
      setDailyStreak(result.daily_streak || 0);
    
      if (data.length === 0) {
        setHasData(false);
        setStepsData([]);
        setLabels([]);
        return;
      }

      setHasData(true);
      setRecommended(data[0]?.recommended || 10000);
      setStepsData(data.map(d => d.value));
      setLabels(data.map(d =>
        parseLocalDate(d.date).toLocaleDateString('en-US', { weekday: 'short' })
      ));
      setPercentages(data.map(d => d.percentage ?? null));
      setStatuses(data.map(d => d.status ?? ''));
    } else {
      setHasData(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [range]);

  useEffect(() => {
    if (!chartRef.current || !stepsData.length) return;
    const maxY = Math.max(...stepsData, recommended);
    const yPadding = Math.ceil(maxY * 0.1); // 10% of max as buffer
    const backgroundColors = statuses.map(status => getBarColor(status));
    const chart = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Steps',
          data: stepsData,
          backgroundColor: backgroundColors,
          borderRadius: 6,
        }]
      },
      options: {
         min: 0,
        max: maxY + yPadding,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: val => `${val.toLocaleString()}`
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const index = ctx.dataIndex;
                const stepCount = ctx.raw.toLocaleString();
                const percent = percentages[index] != null ? ` (${percentages[index]}%)` : '';
                const status = statuses[index] ? ` - ${statuses[index]}` : '';
                const recommendedText = recommended ? `Recommended: ${recommended.toLocaleString()} steps` : '';

                return [
                  `Steps: ${stepCount}${percent}${status}`,
                  recommendedText
                ];
              }
            }
          }
        }
      },
      plugins: [{
        id: 'recommendedLine',
        afterDraw(chart) {
          const { ctx, chartArea: { left, right }, scales } = chart;
          const y = scales.y.getPixelForValue(recommended);
          ctx.save();
          ctx.strokeStyle = '#FF6414';
          ctx.setLineDash([4, 4]);
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(left, y);
          ctx.lineTo(right, y);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#FF6414';
          ctx.font = '12px sans-serif';
          ctx.fillText(`Recommended: ${recommended.toLocaleString()} steps`, left + 5, y - 5);
          ctx.restore();
        }
      }]
    });

    return () => chart.destroy();
  }, [stepsData, labels, recommended]);

  const totalSteps = stepsData.reduce((sum, val) => sum + val, 0);

  return (
    <div className="fixed inset-0 bg-orange-primary bg-opacity-20 z-50 flex justify-center items-center">
      <div className="bg-white w-[90%] max-w-3xl rounded-lg p-4 relative">
        <button onClick={onClose} className="absolute top-2 right-4 text-gray-700">✕</button>
        <h2 className="text-xl font-bold mb-2 text-center">Steps Analytics</h2>

        <div className="flex justify-center gap-4 mb-4">
          {['D', 'W'].map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-full border ${
                r === range ? 'bg-orange-primary text-white' : 'bg-gray-200 text-gray-700'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
   {Object.entries(STATUS_COLOR_MAP).map(([status, color]) => (
   <div key={status} className="flex items-center gap-2 text-sm font-medium">
     <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
     {status}
   </div>
 ))}
        {hasData && stepsData.length > 0 ? (
          <div className="w-full h-[300px]">
            <canvas ref={chartRef}></canvas>
          </div>
        ) : (
          <div className="w-full h-[300px] flex items-center justify-center text-gray-500 font-medium">
            No steps data available for this range
          </div>
        )}

        <div className="mt-4 text-center font-medium space-y-1">
          <div>
            <span className="text-gray-700">Total Steps: </span>
            <span className="text-black">{totalSteps.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-700">Average Per Day: </span>
            <span className="text-black">{average.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-700">Daily Streak: </span>
            <span className="text-black">{dailyStreak} days</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepsGraphModal;
